import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInquiry } from '../../../hooks/useInquiry';
import ClientLayout from '../../../components/layout/ClientLayout';
import { InquiryFormData, InquiryCategory } from '../../../types/inquiry';

const InquiryCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { createInquiry } = useInquiry();

  const [formData, setFormData] = useState<InquiryFormData>({
    category: 'general',
    subject: '',
    content: ''
  });

  const [attachments, setAttachments] = useState<File[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 카테고리 옵션
  const categoryOptions = [
    { value: 'general', label: '일반문의', description: '기타 문의사항' },
    { value: 'portville', label: '포트빌', description: '포트빌 입주 및 관리비 관련' },
    { value: 'parking', label: '주차장', description: '화물차 주차장 정기권 관련' },
    { value: 'facility', label: '시설물', description: '시설물 예약 및 이용 관련' },
    { value: 'account', label: '계정', description: '로그인 및 회원정보 관련' }
  ];

  // 우선순위는 관리자만 설정 가능하므로 UI에서 제거, 기본값은 normal

  // 입력값 변경 처리
  const handleInputChange = (field: keyof InquiryFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // 에러 메시지 제거
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // 첨부파일 처리
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const fileArray = Array.from(files);
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['image/', 'application/pdf', 'text/', 'application/msword', 'application/vnd.openxmlformats-officedocument'];

    const validFiles: File[] = [];
    const errorMessages: string[] = [];

    fileArray.forEach(file => {
      // 크기 확인
      if (file.size > maxSize) {
        errorMessages.push(`${file.name}: 파일 크기는 10MB 이하여야 합니다.`);
        return;
      }

      // 타입 확인
      const isValidType = allowedTypes.some(type => file.type.startsWith(type));
      if (!isValidType) {
        errorMessages.push(`${file.name}: 지원되지 않는 파일 형식입니다.`);
        return;
      }

      validFiles.push(file);
    });

    if (errorMessages.length > 0) {
      setErrors(prev => ({ ...prev, attachments: errorMessages.join('\n') }));
    } else if (errors.attachments) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.attachments;
        return newErrors;
      });
    }

    setAttachments(prev => [...prev, ...validFiles].slice(0, 5)); // 최대 5개
  };

  // 첨부파일 제거
  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // 유효성 검사
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.subject.trim()) {
      newErrors.subject = '제목을 입력해주세요.';
    } else if (formData.subject.trim().length < 5) {
      newErrors.subject = '제목은 5자 이상 입력해주세요.';
    }

    if (!formData.content.trim()) {
      newErrors.content = '문의 내용을 입력해주세요.';
    } else if (formData.content.trim().length < 10) {
      newErrors.content = '문의 내용은 10자 이상 입력해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 폼 제출
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setIsSubmitting(true);

      const submitData: InquiryFormData = {
        ...formData,
        attachments: attachments.length > 0 ? attachments : undefined
      };

      const inquiryId = await createInquiry(submitData);

      // 성공 시 목록 페이지로 이동 (더 안전한 방법)
      navigate('/inquiry', {
        replace: true,
        state: {
          message: '문의가 성공적으로 등록되었습니다. 잠시 후 목록에 표시됩니다.',
          newInquiryId: inquiryId
        }
      });
    } catch (error) {
      console.error('문의 생성 실패:', error);
      setErrors({ submit: error instanceof Error ? error.message : '문의 등록에 실패했습니다.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // 파일 크기 포맷팅
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <ClientLayout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* 헤더 */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
            <button
              onClick={() => navigate('/inquiry')}
              className="text-blue-600 hover:text-blue-700"
            >
              고객센터
            </button>
            <span>{'>'}</span>
            <span>문의하기</span>
          </div>
          <h1 className="text-2xl font-bold text-[#1e2c49]">새 문의 작성</h1>
          <p className="text-gray-600 mt-2">
            궁금한 사항이나 문제가 있으시면 언제든 문의해 주세요. 빠른 시일 내에 답변드리겠습니다.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6">
              {/* 카테고리 선택 */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  문의 카테고리 <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {categoryOptions.map((option) => (
                    <label
                      key={option.value}
                      className={`relative flex cursor-pointer rounded-lg border p-4 focus:outline-none ${
                        formData.category === option.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="category"
                        value={option.value}
                        checked={formData.category === option.value}
                        onChange={(e) => handleInputChange('category', e.target.value as InquiryCategory)}
                        className="sr-only"
                      />
                      <div className="flex-1">
                        <div className="flex items-center">
                          <div className="text-sm font-medium text-gray-900">{option.label}</div>
                          {formData.category === option.value && (
                            <svg className="ml-auto h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{option.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>


              {/* 제목 */}
              <div className="mb-6">
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  제목 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.subject ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="문의 제목을 입력해주세요"
                  maxLength={100}
                />
                {errors.subject && (
                  <p className="mt-2 text-sm text-red-600">{errors.subject}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">{formData.subject.length}/100</p>
              </div>

              {/* 내용 */}
              <div className="mb-6">
                <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
                  문의 내용 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => handleInputChange('content', e.target.value)}
                  rows={8}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none ${
                    errors.content ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="문의하실 내용을 자세히 작성해주세요. 구체적인 정보를 제공해주시면 더 정확한 답변을 받으실 수 있습니다."
                  maxLength={2000}
                />
                {errors.content && (
                  <p className="mt-2 text-sm text-red-600">{errors.content}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">{formData.content.length}/2000</p>
              </div>

              {/* 첨부파일 */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  첨부파일 (선택사항)
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                    accept="image/*,.pdf,.doc,.docx,.txt"
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer flex flex-col items-center justify-center"
                  >
                    <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    <span className="text-sm text-gray-600">파일을 선택하거나 드래그해주세요</span>
                    <span className="text-xs text-gray-500 mt-1">이미지, PDF, 문서 파일 (최대 10MB, 5개까지)</span>
                  </label>
                </div>

                {errors.attachments && (
                  <p className="mt-2 text-sm text-red-600 whitespace-pre-line">{errors.attachments}</p>
                )}

                {/* 첨부된 파일 목록 */}
                {attachments.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-sm font-medium text-gray-700">첨부된 파일:</p>
                    {attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                        <div className="flex-1">
                          <span className="text-sm text-gray-700">{file.name}</span>
                          <span className="text-xs text-gray-500 ml-2">({formatFileSize(file.size)})</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 제출 오류 */}
              {errors.submit && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{errors.submit}</p>
                </div>
              )}

              {/* 안내사항 */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-2">문의 전 확인사항</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• 문의하신 내용에 따라 답변까지 1-3일 소요될 수 있습니다.</li>
                  <li>• 긴급한 사안의 경우 제목에 [긴급] 표시를 해주시거나 내용에 명시해주세요.</li>
                  <li>• 첨부파일은 개인정보가 포함되지 않도록 주의해주세요.</li>
                  <li>• 구체적인 정보를 제공해주시면 더 정확한 답변을 받으실 수 있습니다.</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 제출 버튼 */}
          <div className="mt-6 flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate('/inquiry')}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.subject.trim() || !formData.content.trim()}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  문의 등록 중...
                </div>
              ) : (
                '문의 등록'
              )}
            </button>
          </div>
        </form>
      </div>
    </ClientLayout>
  );
};

export default InquiryCreatePage;